// /src/hooks/useBaselinesData.js
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";

import {
	retrieveBaselines,
	retrieveOneBaseline,
	retrieveOneBaselineByUuid,
	setSelectedBaseline,
} from "../slices/baselineSlice";
import API from "../http-common"; // shared axios instance

import { debugLog, errorLog } from "../utils/debugUtils";
import { prepareBaselineObject } from "../utils/baselineUtils";
import { isObject } from "../utils/dataUtils";

// Custom hook centralizes Baseline API logic
export const useBaselinesData = () => {
	const dispatch = useDispatch();

	// Fetch all baselines (for BaselineList)
	const fetchBaselines = useCallback(() => {
		debugLog(3, "[useBaselinesData][ACTION] fetchBaselines", {});
		debugLog(2, "[useBaselinesData][FLOW] retrieveBaselines started", {});

		trackPromise(
			dispatch(retrieveBaselines())
				.unwrap()
				// Logging for debugging; you could handle errors or show notifications here
				.then((response) => {
					// Baseline list now in Redux: state.baselines.list
					debugLog(3, "[useBaselinesData][STATE] baselines retrieved", { baselines: response });
				})
				.catch((err) => {
					errorLog(2, "[useBaselinesData][FLOW] retrieveBaselines failed", { error: err });
					console.error("Failed to fetch baselines", err);
				})
		);
	}, [dispatch]);

	// Fetch single baseline by id or uuid (for deep linking or details)
	const fetchBaselineByIdOrUuid = useCallback(
		(idOrUuid) => {
			debugLog(3, "[useBaselinesData][ACTION] fetchBaselineByIdOrUuid", { idOrUuid: idOrUuid });
			debugLog(2, "[useBaselinesData][FLOW] retrieveOneBaseline started", { idOrUuid: idOrUuid });

			const action = isNaN(idOrUuid)
				? retrieveOneBaselineByUuid(idOrUuid)
				: retrieveOneBaseline(idOrUuid);

			trackPromise(
				dispatch(action)
					.unwrap()
					.then((response) => {
						// Prepare and set the baseline in Redux (selectedItem)
						const preparedBaseline = prepareBaselineObject(response);
						debugLog(3, "[useBaselinesData][STATE] baseline retrieved", {
							preparedBaseline: preparedBaseline,
						});
						dispatch(setSelectedBaseline(preparedBaseline));
					})
					.catch((err) => {
						errorLog(2, "[useBaselinesData][FLOW] retrieveOneBaseline failed", {
							idOrUuid: idOrUuid,
							error: err,
						});
						console.error("Failed to fetch baseline", err);
					})
			);
		},
		[dispatch]
	);

	/**
	 * Silent fetch for export: returns prepared baseline without touching Redux.
	 * Accepts id or uuid (same as fetchBaselineByIdOrUuid), with opt-in photos.
	 */
	const fetchBaselineForExport = useCallback(async (idOrUuid, { includePhotos = true } = {}) => {
		debugLog(3, "[useBaselinesData][ACTION] fetchBaselineForExport", {
			idOrUuid,
			includePhotos,
		});
		debugLog(2, "[useBaselinesData][FLOW] baseline export API started", { idOrUuid, includePhotos });
		const byUuid = isNaN(idOrUuid);
		const url = byUuid ? `/baselines/uuid/${idOrUuid}` : `/baselines/${idOrUuid}`;
		// Use trackPromise so your global loader can reflect work-in-progress
		const { data } = await trackPromise(API.get(url, { params: { includePhotos } }));
		const prepared = prepareBaselineObject(data);
		debugLog(4, "[useBaselinesData][PROCESS] fetchBaselineForExport prepared", {
			items: prepared?.baseline_items?.length ?? 0,
			includePhotos,
		});
		return prepared;
	}, []);

	// UI handler for list selection; normalizes baseline and updates Redux
	const handleSelectBaseline = useCallback(
		(baseline) => {
			debugLog(3, "[useBaselinesData][ACTION] handleSelectBaseline", { baseline: baseline });

			const id = isObject(baseline) ? baseline.id : baseline;
			fetchBaselineByIdOrUuid(id);
		},
		[fetchBaselineByIdOrUuid]
	);

	return {
		fetchBaselines,
		fetchBaselineByIdOrUuid,
		fetchBaselineForExport,
		handleSelectBaseline,
	};
};
